import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, BarChart3, Eye, TrendingUp, ArrowRight, CheckCircle, Bot, Lightbulb } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ServiceSchema } from '../../components/SchemaMarkup';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveIntelligencePage: React.FC = () => {
  useSEO('cognitive-intelligence');
  const { isBannerVisible, bannerHeight } = useBanner();

  const features = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Machine Learning Models",
      description: "Custom ML models for predictive analytics, classification, regression, and pattern recognition tailored to your business needs."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Advanced Analytics",
      description: "Deep data analysis with statistical modeling, trend analysis, and business intelligence dashboards."
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: "Natural Language Processing",
      description: "AI-powered text analysis, sentiment analysis, chatbots, and automated content generation."
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Computer Vision",
      description: "Image recognition, object detection, facial recognition, and visual quality control systems."
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: "Recommendation Systems",
      description: "Personalized recommendation engines that improve customer experience and drive sales."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Predictive Analytics",
      description: "Forecast future trends, customer behavior, and market dynamics using advanced AI algorithms."
    }
  ];

  const aiCapabilities = [
    { title: "Deep Learning", description: "Neural networks for complex pattern recognition" },
    { title: "Reinforcement Learning", description: "AI that learns through trial and error" },
    { title: "Ensemble Methods", description: "Combining multiple models for better accuracy" },
    { title: "Time Series Analysis", description: "Forecasting based on historical data patterns" },
    { title: "Anomaly Detection", description: "Identifying unusual patterns and outliers" },
    { title: "Optimization Algorithms", description: "Finding optimal solutions to complex problems" }
  ];

  const benefits = [
    "Improve decision-making accuracy significantly",
    "Automate complex analytical processes",
    "Reduce operational costs through optimization",
    "Improve customer satisfaction with personalization",
    "Identify new business opportunities",
    "Enhance risk management and fraud detection",
    "Scale data processing capabilities",
    "Generate actionable insights in real-time"
  ];

  const processes = [
    {
      step: 1,
      title: "Data Assessment",
      description: "Evaluate your data quality, structure, and potential for AI implementation while identifying key business objectives."
    },
    {
      step: 2,
      title: "Model Development",
      description: "Design and train custom AI models using your data, incorporating state-of-the-art algorithms and techniques."
    },
    {
      step: 3,
      title: "Testing & Validation",
      description: "Rigorous testing and validation of models designed for accuracy, reliability, and ethical AI practices."
    },
    {
      step: 4,
      title: "Deployment & Monitoring",
      description: "Deploy AI solutions into production with continuous monitoring and model performance optimization."
    }
  ];

  const useCases = [
    {
      industry: "Healthcare",
      applications: ["Medical imaging analysis", "Drug discovery", "Patient risk assessment", "Treatment optimization"]
    },
    {
      industry: "Finance",
      applications: ["Fraud detection", "Credit scoring", "Algorithmic trading", "Risk management"]
    },
    {
      industry: "Retail",
      applications: ["Demand forecasting", "Price optimization", "Customer segmentation", "Inventory management"]
    },
    {
      industry: "Manufacturing",
      applications: ["Quality control", "Predictive maintenance", "Supply chain optimization", "Process automation"]
    }
  ];

  return (
    <>
      <ServiceSchema
        serviceName="Cognitive Intelligence"
        description="AI and machine learning solutions for intelligent business automation and data insights"
        serviceType="Artificial Intelligence"
        url="https://cognitivecrafthub.com/services/cognitive-intelligence"
        features={[
          "Machine Learning Models",
          "Advanced Analytics",
          "Natural Language Processing",
          "Computer Vision",
          "Recommendation Systems",
          "Predictive Analytics"
        ]}
      />
      <Breadcrumb />
      <main className="pb-20">
      {/* Hero Section */}
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
              <Brain className="w-10 h-10 text-white" />
            </div>
            <h1 
              className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
              style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
            >
              Cognitive Intelligence
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              AI & Data Analytics Solutions
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Unlock the power of your data with advanced AI and machine learning solutions. 
              Transform raw information into actionable insights that drive intelligent business decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">AI Capabilities</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Comprehensive artificial intelligence and data science solutions for modern businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <div className="text-blue-600 dark:text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Technologies Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Advanced AI Technologies</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Cutting-edge artificial intelligence techniques and methodologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiCapabilities.map((capability, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-gray-200 dark:border-white/10">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{capability.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Industry Applications</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              AI solutions tailored for various industries and use cases
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{useCase.industry}</h3>
                <ul className="space-y-2">
                  {useCase.applications.map((app, appIndex) => (
                    <li key={appIndex} className="flex items-center text-gray-700 dark:text-gray-300">
                      <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
                      {app}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Benefits of Cognitive Intelligence</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">AI Implementation Process</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Our systematic approach to AI solution development
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{process.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Harness the Power of AI?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Transform your data into intelligent insights and automated solutions 
              that drive your business forward with cutting-edge AI technology.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Explore AI Solutions
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule AI Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};